import FetchHelpers from "../../fetch-helpers";
import {AsyncNotice} from "../../notices/notice";
import {Spinner} from "../../spinner";
import {capitalize} from "../../utilities/general";

// The number of characters a tag is allowed to have
const INPUT_TEXT_LIMIT = 50;

/**
 * Sort the user tag elements.
 *
 * @param event
 *
 * @returns {Promise<string>}
 */
async function sortHandler(event) {
    event.preventDefault();

    const { dataset: { sortBy, sortDirection } } = event.target;
    const toggler = {'asc': 'desc', 'desc': 'asc'};

    let direction = sortDirection;

    // if the selected sort option already has a class set, we need to toggle the direction
    if(event.target.closest('li').classList.length > 0) {
        direction = toggler[sortDirection];
    }

    // sort the tags
    const existingTagsDiv = document.querySelector('.existing-tags');
    if(existingTagsDiv) {
        [...existingTagsDiv.children]
            .sort(tagsSortingFunction(sortBy, direction, 'element'))
            .forEach(node => existingTagsDiv.appendChild(node));
    } else {
        console.log('Could not sort tags.');
    }

    event.target.dataset.sortDirection = direction;
    return direction;
}

/**
 * Close and reset the save modal
 *
 * @param saveTagModal
 */
function resetAndCloseSaveModal(saveTagModal) {
    // remove displayed tag copy, if present
    const copyTagWrapper = saveTagModal.querySelector('#copy-tag-wrapper');
    if(copyTagWrapper) {
        copyTagWrapper.classList.add('hide');
        copyTagWrapper.childNodes.forEach((element) => {
            element.remove();
        })
    }

    if(saveTagModal) {
        // remove ignore attribute
        saveTagModal.removeAttribute('ignoreClick');

        // ensure info modals are hidden
        const newTagInfoModal = saveTagModal.querySelector('#new-tag-info');
        if(newTagInfoModal) {
            newTagInfoModal.style.display = 'none';
        }

        const modifyTagInfoModal = saveTagModal.querySelector('#modify-tag-info');
        if(modifyTagInfoModal) {
            modifyTagInfoModal.style.display = 'none';
        }

        // hide the modal
        saveTagModal.classList.add('hide');

        // reset text input
        const textInput = saveTagModal.querySelector('input[type="text"]');
        if (textInput) {
            textInput.value = '';
        }

        // reset new tag div
        const newTagDiv = findNewTagElement();
        if(newTagDiv) {
            // reset classes
            newTagDiv.classList.remove(...newTagDiv.classList);
            newTagDiv.classList.add('tag', 'default');

            // reset data attributes
            newTagDiv.setAttribute('data-tag-color', 'default');
            newTagDiv.setAttribute('data-tag-text', '');
            newTagDiv.setAttribute('data-tag-id', '');

            // clear text
            const tagSpan = newTagDiv.querySelector('.tag-text');
            if(tagSpan) {
                tagSpan.textContent = '';
            }

            // clear tag copy
            const tagCopy = saveTagModal.querySelector('[data-tag-copy]');
            if(tagCopy) {
                tagCopy.classList.remove(...tagCopy.classList);
                tagCopy.classList.add('tag','copy');
                tagCopy.textContent = '';
            }
        }

        enableAddNewButton();
    }
}

/**
 * Set the text count to red when it exceeds INPUT_TEXT_LIMIT; Remove color
 * if it is within the limit.
 *
 * @param text
 * @param textCountSpan
 */
function manageTextCountElement(text, textCountSpan) {
    if(text.length === INPUT_TEXT_LIMIT) {
        textCountSpan.textContent = text.length;
        if (!textCountSpan.classList.contains('red')) {
            textCountSpan.classList.add('red');
        }
    } else {
        textCountSpan.textContent = text.length;
        textCountSpan.classList.remove('red');
    }
}

/**
 * Event handler - text input keyup
 *
 * @param event
 * @returns {Promise<void>}
 */
async function saveModalInputTextHandler(event) {
    event.preventDefault();

    const saveTagModal = document.querySelector('#save-tag-modal');
    const { target: { value: text } } = event;
    const newTagDiv = findNewTagElement();
    const textCountSpan = saveTagModal.querySelector('.text-count span');
    const noticeDiv = document.querySelector('[data-notice]');
    const noticeElement = noticeDiv?.querySelector('.tm-notice');

    // manage input length control
    if(text.length > INPUT_TEXT_LIMIT) {
        event.target.value = text.slice(0, INPUT_TEXT_LIMIT);
    }  else {
        manageTextCountElement(text, textCountSpan);

        if (event.key === 'Enter') {
            await saveButtonHandler();
        } else {
            disableSaveButton();
            if (text.length === 0) {
                if (noticeDiv) {
                    noticeElement.textContent = 'No changes made';
                    noticeDiv.classList.remove('hide');
                }
            }

            manageModalElementsOnInputChange(newTagDiv, text);
        }
    }
}

/**
 * Set the save modal title.
 * @param title
 */
function setModalTitle(title) {
    const modalTitle = document.querySelector('[data-modal-title] span');

    if(modalTitle) {
        modalTitle.textContent = title;
    }
}

/**
 * Manage which info modal we display when clicking on the info icon by the save modal title
 * @param action
 */
function showTitleToolTip(action) {
    const info_link = document.querySelector('[data-modal-title] a');

    if (action === 'create') {
        info_link.removeEventListener('click', showModifyInfoModal);
        info_link.addEventListener('click', showCreateInfoModal);
    }

    // show 'modify' info modal
    if (action === 'modify') {
        info_link.removeEventListener('click', showCreateInfoModal);
        info_link.addEventListener('click', showModifyInfoModal);
    }
}

/**
 * Event handler for showing the "create" info modal in the save modal
 * @param event
 */
function showCreateInfoModal(event) {
    event.preventDefault();

    const create_info_modal = document.querySelector('[data-create-info-modal]');
    const modify_info_modal = document.querySelector('[data-modify-info-modal]');

    if(create_info_modal && modify_info_modal) {
        create_info_modal.style.display = 'inline';
        modify_info_modal.style.display = 'none';
    }
}

/**
 * Event handler for showing the "modify" info modal in the save modal
 *
 * @param event
 */
function showModifyInfoModal(event) {
    event.preventDefault();

    const create_info_modal = document.querySelector('[data-create-info-modal]');
    const modify_info_modal = document.querySelector('[data-modify-info-modal]');

    if(create_info_modal && modify_info_modal) {
        create_info_modal.style.display = 'none';
        modify_info_modal.style.display = 'inline';
    }
}

/**
 * Toggles the visibility of the save tag modal and the 'Add New' button when appropriate.
 *
 * @param saveTagModal
 * @param triggerElement (Either the 'Add new' button or an existing tag, that has been clicked on)
 */
function manageSaveModalVisibility(saveTagModal, triggerElement) {
    if(saveTagModal) {
        if(saveTagModal.classList.contains('hide')) {
            // determine if triggerElement should be disabled

            if(triggerElement.id === 'new-tag-button') {
                triggerElement.classList.add('disabled');
                triggerElement.setAttribute('disabled', 'disabled');
            }

            // show new tag modal
            saveTagModal.classList.remove('hide');

            // set focus
            const inputElement = saveTagModal.querySelector('input#save-tag');
            if(inputElement) {
                inputElement.focus();
            }
        }
    }
}

/**
 * Enable the 'Add New' tag button.
 */
function enableAddNewButton() {
    const newTagButton = document.querySelector('#new-tag-button');

    if(newTagButton) {
        newTagButton.classList.remove('disabled');
        newTagButton.removeAttribute('disabled');
    }
}

/**
 * Disable the 'Add New' tag button.
 *
 */
function disableAddNewButton() {
    const newTagButton = document.querySelector('#new-tag-button');

    if(newTagButton) {
        newTagButton.classList.add('disabled');
        newTagButton.setAttribute('disabled', 'disabled');
    }
}

/**
 * Disable the 'Save' button in the Save modal.
 */
function disableSaveButton() {
    const saveButton = document.querySelector('#save-button');
    if (saveButton) {
        saveButton.classList.add('disabled');
        saveButton.setAttribute('disabled', 'disabled');
    }
}

/**
 *  Enable the 'Save' button in the Save modal.
 *
 */
function enableSaveButton() {
    const saveButton = document.querySelector('#save-button');
    if (saveButton) {
        saveButton.classList.remove('disabled');
        saveButton.removeAttribute('disabled');
    }
}

/**
 * Disable the 'Delete' button
 */
function disableDeleteButton() {
    const deleteButton = document.querySelector('#delete-button');
    if(deleteButton) {
        deleteButton.classList.add('disabled');
        deleteButton.setAttribute('disabled', 'disabled');
    }
}

/**
 * Enable the 'Delete' button on the save modal.
 */
function enableDeleteButton() {
    const deleteButton = document.querySelector('#delete-button');
    if(deleteButton) {
        deleteButton.classList.remove('disabled', 'hide');
        deleteButton.removeAttribute('disabled');
    }
}

/**
 * Hide the delete button in the save modal.
 */
function hideDeleteButton() {
    const deleteButton = document.querySelector('#delete-button');
    if(deleteButton) {
        deleteButton.classList.add('hide');
    }
}

/**
 * Initialize the save modal for tag creation:
 * - Sets the modal title
 * - Sets the modal action property to 'create'
 * - Ensures default color selected and empty input text field.
 * - set handler for save button
 *
 * @param saveTagModal
 */
function prepSaveModalForTagCreation(saveTagModal) {
    const textCountSpan = saveTagModal.querySelector('.text-count span');
    textCountSpan.textContent = '0';

    saveTagModal.dataset.action = 'create';
    setModalTitle('Add New Tag');
    showTitleToolTip('create');

    const form = document.querySelector('#annotations-form');
    if(form) {
        form.remove();
    }

    clearSaveModal(saveTagModal);
    disableAddNewButton();
    hideDeleteButton();
}

/**
 * Clear elements on the save modal.
 none*
 * @param saveTagModal
 */
function clearSaveModal(saveTagModal) {
    // remove the tag copy
    const tagCopy = document.querySelector('[data-tag-copy]');
    if(tagCopy) {
        tagCopy.remove();
    }

    // remove tag copy tooltip
    // const tagCopyTooltip = document.querySelector('#tag-copy-tooltip');
    // if(tagCopyTooltip) {
    //   tagCopyTooltip.remove();
    // }

    // clear the text field
    const inputElement = saveTagModal.querySelector('input#save-tag');
    inputElement.value = '';

    // clear new tag div
    const newTagDiv = findNewTagElement();
    if(newTagDiv) {
        newTagDiv.classList.remove(...newTagDiv.classList);
        newTagDiv.classList.add('tag', 'default', 'hide');
    }

    // clear notice text
    const noticeDiv = document.querySelector('[data-notice]');
    const noticeSpan = noticeDiv.querySelector('.tm-notice');
    if(noticeSpan) {
        noticeSpan.textContent = '';
    }

    const count = document.querySelector('#annotations-count')
    if(count) {
        count.textContent = '0';
    }

    // remove tag copy form
    const form = document.querySelector('#annotations-form');
    if(form) {
        form.remove();
    }

    setColorSelector('default');
    manageModalElementsOnInputChange(newTagDiv, '');
}

/**
 * Initialize the save modal to edit a tag.
 * - Sets the modal title
 * - renders the tag to be updated
 * - Enable the Delete button
 * - Set the input text value and the colors selector 'selected' color to the text and color
 *   of the tag we are going to edit.
 *
 * @param saveTagModal
 * @param tagToUpdate
 */
function prepSaveModalForTagEditing(saveTagModal, tagToUpdate) {
    clearSaveModal(saveTagModal);

    const textCountSpan = saveTagModal.querySelector('.text-count span');
    const inputElement = saveTagModal.querySelector('input#save-tag');
    const newTagDiv = findNewTagElement();

    saveTagModal.dataset.action = 'update';
    setModalTitle('Modify Tag');
    showTitleToolTip('modify');

    /* display tag being updated */
    const tagCopy = createCopyOfExistingTag(extractTagProperties(tagToUpdate), true, true);
    const {dataset: {tagColor: color, tagId: id, tagText: text, tagUsage: usage}} = tagCopy;
    const copyTagWrapper = saveTagModal.querySelector('#copy-tag-wrapper');
    if (copyTagWrapper) {
        copyTagWrapper.classList.remove('hide');
        copyTagWrapper.appendChild(tagCopy);
    }

    if (parseInt(usage) > 0) {
        // make info icon visible
    }

    // set text input
    if (inputElement) {
        inputElement.value = text;
        textCountSpan.textContent = text.length;

        // handle counter color
        textCountSpan.classList.remove('red');
        if (text.length >= INPUT_TEXT_LIMIT) {
            textCountSpan.classList.add('red');
        }
    }

    // set current color on color selector
    setColorSelector(color);

    if (newTagDiv) {
        // set color class
        newTagDiv.classList.remove('default');
        newTagDiv.classList.add(color)

        // set new tag div properties
        newTagDiv.setAttribute('data-tag-color', color);
        newTagDiv.setAttribute('data-tag-id', id);
        newTagDiv.setAttribute('data-tag-text', text);

        // display text
        const tagSpan = document.querySelector('.new-tag .tag-text');
        if (tagSpan) {
            tagSpan.textContent = text;
        }

        // display div
        newTagDiv.classList.remove('hide');
    }

    disableAddNewButton();
    disableSaveButton();
    enableDeleteButton();
}

/**
 * Handles the click on the save modal 'Save' button.
 * Figures out if we are creating a new tag or updating an existing tag and hands off control to the appropriate
 * function to handle the action.
 *
 */
async function saveButtonHandler() {
    const saveButton = document.querySelector('#save-button');
    const saveTagModal = document.querySelector('#save-tag-modal');
    const noticeDiv = document.querySelector('[data-notice]');
    const noticeElement = noticeDiv?.querySelector('.tm-notice');
    const inputTextElement = document.querySelector('#save-tag');

    const existingTag = findTagOnPageByText(inputTextElement.value);
    const existingProps = extractTagProperties(existingTag);

    const newTagDiv = findNewTagElement();
    const newProps = extractTagProperties(newTagDiv);

    if(inputTextElement.value.length === 0) {
        if (noticeElement) {
            noticeDiv.classList.remove('hide');
            noticeElement.textContent = 'Enter a tag name before submitting';
            return;
        }
    }

    if (existingProps.text === newProps.text && existingProps.color === newProps.color) {
        if (noticeElement) {
            noticeDiv.classList.remove('hide');
            noticeElement.textContent = 'No changes made';
            return;
        }
    } else if (inputTextElement.value.length === 0) {
        if (noticeElement) {
            noticeDiv.classList.remove('hide');
            noticeElement.textContent = 'Enter a tag name before submitting';
            return;
        }
    }
    if(saveTagModal) {
        let { dataset: { action } } = saveTagModal;

        // guard clause
        if (!action || !['create', 'update'].includes(action)) { console.log("Don't know if we are creating or updating...");
            return;
        }

        const newTagDiv = findNewTagElement();
        if (saveButton && !saveButton.classList.contains('disabled')) {
            if (action === 'create') {
                await createTag(newTagDiv);
            }

            if (action === 'update') {
                // at this point, the new tag div encapsulates the changes to the tag we're updating
                await updateTag(newTagDiv);
            }
        }
    }

    resetAndCloseSaveModal(saveTagModal);
}

/**
 * Sets the color selector element to a given color.
 *
 * @param color
 */
function setColorSelector(color) {
    const saveTagModal = document.querySelector('#save-tag-modal');
    const colorsWrapper = saveTagModal.querySelector('.tag-colors-wrapper');

    if (colorsWrapper) {
        const currentlySelected = colorsWrapper.querySelector('.selected');
        const toSelect = colorsWrapper.querySelector(`[data-tag-color="${color}"]`);

        // if we have the right color selected
        if (currentlySelected === toSelect) {
            return;
        }

        currentlySelected.classList.remove('selected');
        toSelect.classList.add('selected');
    }
}

/**
 * Check if a user tag already exists with the text. If id provided, search by id.
 *
 * @param tagText
 * @param tagId
 * @returns {*|null}
 */
function findTagOnPage(tagText, tagId = null) {
    const existingTagsCollection = document.querySelector('.existing-tags');
    const action = existingTagsCollection?.dataset?.action;
    let selector = ".existing-tags .tag:not([data-tag-type='delete'])";

    // if there is a suggested tags collection on the page, add those tags
    const suggestedTagsCollection = document.querySelector('.suggested-tags');
    if (suggestedTagsCollection) {
      selector = `${selector}, .suggested-tags .tag:not([data-tag-type='delete'])`;
    }

    // On Update, include the copy-tag element in the list of tags before search
    if (existingTagsCollection && action === 'update') {
        selector = ".existing-tags .tag, [data-tag-copy]";
    }

    const tags = document.querySelectorAll(selector);
    if (tags) {
        const allTags = Array.from(tags);
        const existingTag = allTags.find((tag) => {
            let {dataset: {tagId: id, tagText: text}} = tag;

            // search by id
            if (tagId && id) {
                return tagId === id;
            }

            // search by tag text
            return tagText === text; // && newTagColor === color;
        })

        if (existingTag) {
            return existingTag;
        }
    }

    return null;
}

/**
 * Find a tag on the page by tag text.
 *
 * @param text
 * @returns {*|null}
 */
function findTagOnPageByText(text) {
    return findTagOnPage(text);
}

/**
 *
 * Find tag on page by tag id.
 * @param id
 * @returns {*|null}
 */
function findTagOnPageById(id) {
    return findTagOnPage('', id);
}

/**
 * Find the element for creating a new tag.
 *
 * @returns {Element|null}
 */
function findNewTagElement() {
    let element;
    if( isTagManagementOnReader()) {
       element = document.querySelector('.tag[data-type="new"]');
    } else {
        element = document.querySelector('.tag[data-save-tag]');
    }

    if(element) {
        return element;
    }

    return null;
}

/**
 * Manage elements in Save modal whenever the text in the input text field change.
 * - Update the new tag div with text from text input.
 * - If the tag exists already, disable the 'Add' button and
 * - Display a notice when appropriate.
 *
 * @param newTagDiv
 * @param newTagText
 */
function manageModalElementsOnInputChange(newTagDiv, newTagText) {
    const noticeDiv = document.querySelector('[data-notice]');
    const noticeElement = noticeDiv.querySelector('.tm-notice');

    const saveModal = document.querySelector('#save-tag-modal');
    const action = saveModal.getAttribute('data-action');

    const tagSpan = document.querySelector('.tag-text');
    const existingTag = findTagOnPageByText(newTagText);
    // let noticeTagElement = null;

    if(noticeDiv) {
        // removeTagExistsNoticeElement();
        noticeElement.textContent = '';
    }

    // Set tag text and icon
    if(tagSpan) {
        tagSpan.textContent = newTagText;
    }

    newTagDiv.setAttribute('data-tag-text', newTagText);

    if(newTagText.length > 0) {
        disableAddNewButton();
        newTagDiv.classList.remove('hide');

        // if a tag already exists with the text in the input field
        let existingTagWithSameText = findTagOnPageByText(newTagText);
        if(existingTagWithSameText) {
            const props = extractTagProperties(existingTagWithSameText);
            // noticeTagElement = createCopyOfExistingTag(props, true);
        }

        if(existingTagWithSameText) {
            let { dataset: {tagText: existingText, tagId: existingId } } = existingTagWithSameText;
            let { dataset: { tagColor: newColor, tagText: newText } } = newTagDiv;

            if(action === 'create') {
                // the text of new tags should be unique
                if (existingText === newText) {
                    spotlightTag(existingTag);
                    disableSaveButton();
                    noticeElement.textContent = "Tag already exists";
                    noticeDiv.classList.remove('hide');
                } else {
                    removeSpotlightFromTags(existingTag);
                    noticeElement.textContent = '';
                    noticeDiv.classList.remove('hide');
                    enableSaveButton();
                }
            }

            if(action === 'update') {
                const copyOfThisTag = saveModal.querySelector('[data-tag-copy]');
                let { dataset: { tagColor: thisColor, tagText: thisText, tagId: thisId } } = copyOfThisTag;

                const textChanged = thisText !== newText;
                const colorChanged = thisColor !== newColor;
                const noChangeToCurrentTag = (thisId === existingId) && !textChanged && !colorChanged;
                const changesToCurrentTag = (thisId === existingId) && (textChanged || (colorChanged && !textChanged));

                if (noChangeToCurrentTag) {
                    spotlightTag(existingTag);
                    disableSaveButton();
                    noticeElement.textContent = 'No change';
                    noticeDiv.classList.remove('hide');
                    // noticeDiv.insertBefore(noticeTagElement, noticeDiv.firstChild);
                    disableElement(newTagDiv);
                } else if (changesToCurrentTag) {
                    removeSpotlightFromTags(existingTag);
                    noticeDiv.classList.add('hide');
                    enableSaveButton();
                    // removeTagExistsNoticeElement();
                    enableElement(newTagDiv);
                } else {
                    spotlightTag(existingTag);
                    disableSaveButton();
                    noticeElement.textContent = "Tags must be uniquely named";
                    noticeDiv.classList.remove('hide');
                    disableElement(newTagDiv);
                    //spotlight existing tag
                }
            }
        }
        // if the text for the new tag is unique - allow update
        else {
            removeSpotlightFromTags();
            noticeDiv.classList.add('hide');
            enableSaveButton();
            enableElement(newTagDiv);
        }
    } else {
        removeSpotlightFromTags(existingTag);
        // if newTagText.length === 0
        newTagDiv.classList.add('hide');
        noticeDiv.classList.add('hide');
    }
}

/**
 * Make a copy of the tag div to display in the notice div.
 *
 * @param tagProperties
 * @param showUsage
 * @param withLinkToAnnotationsPage
 * @param withCopyDataAttribute
 */
function createCopyOfExistingTag(
  tagProperties,
  showUsage = false,
  withLinkToAnnotationsPage = false,
  withCopyDataAttribute = true) {
    const { color, id, lastUpdated, text, usage } = tagProperties;
    const tag = document.createElement('div');

    if(withCopyDataAttribute) {
        tag.setAttribute('data-tag-copy', '');
    }

    tag.setAttribute('data-tag-color', color);
    tag.setAttribute('data-tag-id', id);
    tag.setAttribute('data-tag-text', text);
    tag.setAttribute('data-tag-last-updated', lastUpdated);
    tag.setAttribute('data-tag-usage', usage);
    tag.setAttribute('tab-index', '0');

    // <span class='tag-usage'>{$tag['Usage']}</span>
    tag.classList.add('tag', 'copy', `${color}`);
    const span = document.createElement('span');
    span.textContent = text;
    tag.appendChild(span);

    if(showUsage) {
        // tag.textContent = text;
        const usageSpan = document.createElement('span');
        usageSpan.classList.add('tag-usage');
        usageSpan.textContent = usage;
        tag.appendChild(usageSpan);
    }

    if(withLinkToAnnotationsPage) {
        if(parseInt(usage) > 0) {
            const copyTagWrapper = document.querySelector('#copy-tag-wrapper');
            let form = document.querySelector('#annotations-form');
            if (!form) {
                form = document.createElement('form');
                const hidden = document.createElement('input');
                hidden.setAttribute('type', 'hidden');
                hidden.setAttribute('name', 'tagId');
                hidden.setAttribute('value', id);

                // create form for link to annotations page
                let {dataset: {url}} = copyTagWrapper;

                form.id = 'annotations-form';
                form.setAttribute('method', 'POST');
                form.setAttribute('action', url);
                form.appendChild(hidden);

                copyTagWrapper.parentElement.append(form);
                tag.addEventListener('click', (event) => {
                    event.preventDefault();
                    if(confirm("By clicking on the 'Continue' button you will be taken to the\n" +
                        "Annotations page filtered by this tag. Any unsaved changes to this tag will be lost.")) {

                        const spinner
                            = new Spinner('filter-spinner', '.annotation-options', 'afterbegin');
                        spinner.add();

                        form.submit();
                    }
                })
            }
        }
    }

    return tag;
}

/**
 * Look for and return the tag in the notice div that is a copy of an existing user tag.
 *
 * @returns {Element|null}
 */
function findTagCopyElementInNoticeDiv() {
    const noticeElement = document.querySelector('[data-notice]')
    if(noticeElement) {
        const copyElement = noticeElement.querySelector('[data-tag-copy]');
        if(copyElement) {
            return copyElement;
        }
    }

    return null;
}

/**
 * Find the element that is a copy of the tag being updated.
 *
 * @returns {*}
 */
function findCurrentTagCopyInSaveModal() {
    const copyElement= document.querySelector('#save-tag-modal [data-tag-copy]');

    if(copyElement) {
        return copyElement;
    }

    return null;
}

/**
 * Remove copy of existing tag in notice div.
 */
// function removeTagExistsNoticeElement() {
//   const copyTag = findTagCopyElementInNoticeDiv();
//   if(copyTag) {
//     copyTag.remove();
//   }
// }

/**
 * Handle adding a new tag from the tags management page
 *
 * @param tag
 * @returns {Promise<void>}
 */
async function createTag(tag) {
    const action = 'create';

    await persistTag(action, tag);
}

/**
 * Delete the selected tag.
 *
 * @param tag
 * @returns {Promise<void>}
 */
async function deleteTag(tag) {
    const action = 'delete';

    await persistTag(action, tag);
}

/**
 * Set up call to persist tag update.
 *
 * @param tag
 * @returns {Promise<void>}
 */
async function updateTag(tag) {
    const action = 'update';

    await persistTag(action, tag);
}

/**
 * Persist the tag data to the database.
 *
 * @param action
 * @param tag
 * @returns {Promise<void>}
 */
async function persistTag(action, tag) {
    const spinner = new Spinner('save-spinner', '.annotation-options', 'afterbegin');

    try {
        const saveModal = document.querySelector('#save-tag-modal');
        let {dataset: {tagColor: color, tagId: id = null, tagText: text}} = tag;

        // ensure the tag not longer than the limit
        text = text.slice(0, INPUT_TEXT_LIMIT);

        const data = {action, id, color, text};
        let {dataset: {url}} = saveModal;

        spinner.add();

        // async fetch
        let result = await new FetchHelpers().fetchData('POST', url, data);
        let {success, messages} = result;

        // handle response
        let msg = '';
        if (success) {
            msg = `${capitalize(action)} action successful.`;

            // if we didn't get back the id of the tag modified grab it from the data variable
            const data_clone = Object.assign({}, data);
            delete data_clone.action;
            if(action === 'create' && !Object.keys(result).includes('created')) {
                result[action_response] = data_clone;
            } else if(action === 'delete' && !Object.keys(result).includes('deleted')) {
                result[action_response] = data_clone;
            } else if(action === 'update' && !Object.keys(result).includes('updated')) {
                result[action_response] = clone;
            }

            updateTagCollection(action, result);
        }

        // handle messages
        if(messages) {

            messages.forEach((message) => {
                console.log(message);
                msg += `${message}\n`;
            })
            msg.replaceAll("\n", '<br />');
        }

        const type = result['success'] ? 'info' : 'warning';
        const notice = new AsyncNotice(msg, type);

        await notice.create({}, true);
        notice.remove();
    } finally {
        spinner.remove();
    }
}

function updateTagCollection(action, result) {
    const tagsCollection = document.querySelector('.existing-tags');

    let tag;

    if(action === 'create') {
        // add tag in current sort position
        let { created } = result;
        created.forEach((tag) => {
            const tagElement = createCopyOfExistingTag(tag, true);
            tagElement.classList.remove('copy');
            tagElement.removeAttribute('data-tag-copy');

            tagElement.addEventListener('click', (event) => {
                existingTagClickEventHandler(event);
            })

            tagsCollection.appendChild(tagElement);
        })

        const sortElement = document.querySelector('#sort-tags');
        const sortSelected = sortElement.querySelector('li[class] a');

        if(sortSelected) {
            const { dataset: { sortBy, sortDirection } } = sortSelected;

            [...tagsCollection.children]
                .sort(tagsSortingFunction(sortBy, sortDirection, 'element'))
                .forEach(node => tagsCollection.appendChild(node));
        }
    }

    if(action === 'update') {
        // update the tag
        let { updated } = result;
        if (updated) {
            updated.forEach((tagProperties => {
                tag = findTagOnPageById(tagProperties.id);
                if (tag) {
                    // set up properties of new tag element
                    const existingTagProperties = extractTagProperties(tag);
                    const updatedTag = createCopyOfExistingTag(existingTagProperties, true, false, false);

                    // update color
                    updatedTag.setAttribute('data-tag-color', tagProperties.color);
                    updatedTag.classList.replace(existingTagProperties.color, tagProperties.color);

                    // update last updated
                    updatedTag.setAttribute('data-tag-last-updated', tagProperties.lastUpdated);

                    // update text
                    const textElement = document.createTextNode(tagProperties.text);
                    updatedTag.setAttribute('data-tag-text', tagProperties.text);
                    updatedTag.childNodes[0].replaceWith(textElement);

                    // click event handler
                    updatedTag.addEventListener('click', (event) => {
                        existingTagClickEventHandler(event);
                    })

                    tag.replaceWith(updatedTag);
                }
            }))
        }
    }

    if(action === 'delete') {
        // remove the tag
        let { deleted } = result;
        if(deleted) {
            deleted.forEach((id => {
                tag = findTagOnPageById(id);
                if (tag) {
                    tag.remove();
                }
            }))
        }
    }

    // update count
    const total = document.querySelector('.annotation-items #total');
    if(total) {
        const tags = document.querySelectorAll('.existing-tags .tag');
        total.textContent = tags ? tags.length : '--';
    }

    const saveModal = document.querySelector('#save-tag-modal');
    if(saveModal) {
        resetAndCloseSaveModal(saveModal);
    }
}

/**
 * Extract the tag properties from a tag element's dataset. If tagElement is null, return object with
 * blank tag properties.
 *
 * @param tagElement
 * @returns {{lastUpdated, color, usage, id, text}}
 */
function extractTagProperties(tagElement) {
    if(tagElement) {
        let {
            dataset:
                { tagColor: originalColor = '',
                  tagId: id = '',
                  tagLastUpdated: lastUpdated = '',
                  tagText: text = '',
                  tagUsage: usage = '0'
                }
        } = tagElement;
        const color = originalColor.length === 0 ? 'default' : originalColor;

        return {color, id, lastUpdated, text, usage};
    }

    return { color: '', lastUpdated: '', id: '', text: '', usage: '0'}
}

/**
 * Event handler for click on existing tag in tags collection.
 *
 * @param event
 */
function existingTagClickEventHandler(event) {
    event.stopPropagation();

    const saveTagModal = document.querySelector('#save-tag-modal');
    // if modal is showing
    if(!saveTagModal.classList.contains('hide')) {
        // if click is outside the active modal,
        if (!saveTagModal.contains(event.target)) {
            return;
        }
    }

    let tagElement = event.target;
    if(tagElement.tagName === 'SPAN') {
        tagElement = tagElement.parentNode;
    }

    prepSaveModalForTagEditing(saveTagModal, tagElement);
    manageSaveModalVisibility(saveTagModal, tagElement);
}

/**
 * Disable an element by handling the 'disabled' attribute and class.
 *
 * @param element
 */
function disableElement(element) {
    element.setAttribute('disabled', 'true');
    if(!element.classList.contains('disabled')) {
        element.classList.add('disabled');
    }
}

/**
 * Enable an element by handling the 'disabled' attribute and class.
 *
 * @param element
 */
function enableElement(element) {
    element.removeAttribute('disabled');
    element.classList.remove('disabled');
}

/**
 * Visually mark tag as in edit mode.
 *
 * @param tag
 */
function spotlightTag(tag) {
    if (tag) {
        tag.classList.add('highlighted');
        tag.dataset.highlighted = 'true';
        // await asyncSleep(2000, removeSpotlightFromTags(tag);
        // removeSpotlightFromTags(tag);
    }
}

/**
 * Clear highlighted style from tag.
 * - resets border width
 * - clears and hides the new tag element
 * If no target element is passed in, check the page for any elements with the data attribute 'data-highlighted'
 *
 * @param targetElement
 */
function removeSpotlightFromTags(targetElement = null) {
    if (targetElement) {
        targetElement.classList.remove('highlighted');
        targetElement.removeAttribute('highlighted');
    } else {
        const spotLightedCollection = document.querySelectorAll(`[data-highlighted]`);
        if (spotLightedCollection) {
            spotLightedCollection.forEach(
                (tag) => {
                    removeSpotlightFromTags(tag);
                }
            )
        }
    }
}

/**
 * Is the current context the reader or the tags management page.
 * Checks the location pathname.
 *
 * @returns {boolean}
 */
function isTagManagementOnReader() {
    const pathname = window.location.pathname;

    return pathname.includes('reader.php');
}

/**
 * Returns the sorting function to use for sorting tag, based on two criteria, sortBy and direction.
 *
 * @param sortBy - sort attribute ('name','time','usage')
 * @param direction - ('asc', 'desc')
 * @param elementItem
 */
function tagsSortingFunction(sortBy, direction, elementItem = 'array') {
    if(sortBy === 'color') {
        return sortTagsByColorSortingFunction(direction, elementItem);
    } else if (sortBy === 'usage') {
        return sortTagsByMostUsedSortingFunction(direction, elementItem);
    } else if (sortBy === 'time') {
        return sortTagsByMostRecentSortingFunction(direction, elementItem);
    } else {
        return sortTagsByNameSortingFunction(direction, elementItem);
    }
}

/**
 * Sorting function to use with sort() that sorts tags by color.
 * @returns {number}
 */
function sortTagsByColorSortingFunction(direction, elementItem) {
    return generalSortingFunction('color', direction, elementItem);
}

/**
 * Sorting function to use with sort() that sorts tags alphabetically.
 * @returns {number}
 */
function sortTagsByNameSortingFunction(direction, elementItem) {
    return generalSortingFunction('name', direction, elementItem);
}

/**
 * Sorting function to use with sort() that sorts by the creation date of the tags.
 * @returns {number}
 */
function sortTagsByMostRecentSortingFunction(direction, elementItem) {
    return generalSortingFunction('time', direction, elementItem);
}

/**
 * Sorting function to use with sort() that sorts by how many times the tag has been applied to an annotation.
 * @param direction
 * @param elementItem
 * @returns {number}
 */
function sortTagsByMostUsedSortingFunction(direction, elementItem) {
    return generalSortingFunction('usage', direction, elementItem);
}

/**
 * Create an object of tag attributes from a tag Dom element
 * @param tag
 * @returns {{lastUpdated, color, usage, text}}
 */
function transformTagElementData(tag) {
    let { dataset: { tagColor: color, tagLastUpdated: lastUpdated, tagText: text, tagUsage: usage } } = tag;
    return { color, lastUpdated, text, usage };
}

/**
 * Function for sort() method when sorting words (tags).
 *
 * @param {string} type (one of: 'alpha', 'usage', 'time'(one of: 'alpha', 'usage', 'time')
 * @param {string} direction
 * @param elementItem
 * @returns {number}
 */
function generalSortingFunction(type, direction, elementItem= 'array') {
    let first, second, ascending, descending;

    return function (a, b) {
        let firstItem = a;
        let secondItem = b;
        // for all sort options except alpha, do a secondary alpha sort
        let secondaryLevelSort = true;

        if (elementItem === 'element') {
            firstItem = transformTagElementData(a);
            secondItem = transformTagElementData(b);
        }

        if(type === 'color') {
            first = firstItem.color;
            second = secondItem.color;
        } else if (type === 'usage') {
            first = parseInt(firstItem.usage);
            second = parseInt(secondItem.usage);
        } else if (type === 'time') {
            first = Date.parse(firstItem.lastUpdated);
            second = Date.parse(secondItem.lastUpdated);
        } else {
            // default is alpha sort
            secondaryLevelSort = false
            first = firstItem.text.toLowerCase();
            second = secondItem.text.toLowerCase();
        }

        if (direction === 'asc') {
            ascending = first < second;
            descending = first > second;
        } else if (direction === 'desc') {
            ascending = first > second;
            descending = first < second;
        } else {
            // for an invalid sort value, don't change anything.
            return 0;
        }
        // first value comes first
        if (ascending) {
            return -1;
        }

        // second value comes first
        if (descending) {
            return 1;
        }

        // if the values are the same, do the secondary alpha sort
        if(secondaryLevelSort) {
            return String(firstItem.text).localeCompare(String(secondItem.text));
        }

        return 0;
    }
}


/**
 * Helper: check if parent is visible
 */
function isParentVisible(element) {
    return !element.classList.contains('hide') && !element.hasAttribute('disabled');
}

export {
    deleteTag, disableAddNewButton,existingTagClickEventHandler, findCurrentTagCopyInSaveModal, findTagOnPageByText,
    findNewTagElement, saveModalInputTextHandler,manageSaveModalVisibility, manageModalElementsOnInputChange,
    prepSaveModalForTagCreation, prepSaveModalForTagEditing, resetAndCloseSaveModal, saveButtonHandler, sortHandler,
    sortTagsByNameSortingFunction, manageTextCountElement, spotlightTag, removeSpotlightFromTags,
    isParentVisible, isTagManagementOnReader, transformTagElementData, tagsSortingFunction, INPUT_TEXT_LIMIT
};
